import React, { createContext, useState, useContext, useMemo, ReactNode, FC, useEffect, useCallback } from 'react';
import { User, Unit } from '../../core/models/data';
import { resolveUnitTimezone } from '../../core/location/unitLocation';
import { getAllowedUnitIds, getFallbackSelectedUnitIds, getUserScopedUnits } from '../components/unitSelectionGateLogic';

interface UnitContextType {
  allUnits: Unit[];
  selectedUnits: string[];
  selectedUnitObjects: Unit[];
  primaryUnit: Unit | null;
  primaryUnitTimezone: string;
  setSelectedUnits: React.Dispatch<React.SetStateAction<string[]>>;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

interface UnitProviderProps {
  children: ReactNode;
  currentUser: User | null; // Can be null during loading
  allUnits: Unit[];
}
const UNIT_RESTORE_KEY = 'mintleaf.unit.restore';
const UNIT_RESTORE_TTL_MS = 24 * 60 * 60 * 1000;
const isDev = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

type UnitRestorePayload = {
  userId?: string;
  activeUnitIds?: string[];
  savedAt?: number;
};

export const UnitProvider: FC<UnitProviderProps> = ({ children, currentUser, allUnits }) => {
  
  const storageKey = useMemo(() => 
    currentUser ? `mintleaf_selected_units_${currentUser.id}` : null
  , [currentUser]);

  const getInitialSelection = useCallback((): string[] => {
    const allowedUnitIds = getAllowedUnitIds(allUnits, currentUser);
    const selectableUnitIds = new Set(getUserScopedUnits(allUnits, currentUser).map(unit => unit.id));

    if (!currentUser || allowedUnitIds.size === 0) return [];

    // 1. Prefer canonical unit restore payload
    try {
      const rawUnitRestore = localStorage.getItem(UNIT_RESTORE_KEY);
      if (rawUnitRestore) {
        const parsed = JSON.parse(rawUnitRestore) as UnitRestorePayload;
        if (
          parsed.userId === currentUser.id &&
          typeof parsed.savedAt === 'number' &&
          Date.now() - parsed.savedAt <= UNIT_RESTORE_TTL_MS &&
          Array.isArray(parsed.activeUnitIds) &&
          parsed.activeUnitIds.every(item => typeof item === 'string')
        ) {
          const restored = parsed.activeUnitIds.filter(
            item => allowedUnitIds.has(item) && selectableUnitIds.has(item)
          );
          if (restored.length !== parsed.activeUnitIds.length) {
            if (restored.length > 0) {
              localStorage.setItem(UNIT_RESTORE_KEY, JSON.stringify({
                ...parsed,
                activeUnitIds: restored,
                savedAt: Date.now(),
              }));
            } else {
              localStorage.removeItem(UNIT_RESTORE_KEY);
            }
          }
          if (isDev) {
            console.debug('[unit-context] restored selected units from canonical restore', {
              userId: currentUser.id,
              restoredCount: restored.length,
            });
          }
          return restored;
        }
        if (
          parsed.userId === currentUser.id &&
          typeof parsed.savedAt === 'number' &&
          Date.now() - parsed.savedAt > UNIT_RESTORE_TTL_MS
        ) {
          if (isDev) {
            console.debug('[unit-context] unit restore expired', {
              userId: currentUser.id,
              savedAt: parsed.savedAt,
            });
          }
          localStorage.removeItem(UNIT_RESTORE_KEY);
        }
      }
    } catch (e) {
      console.error('Failed to parse unit restore payload', e);
    }

    // 2. Legacy fallback localStorage key
    if (storageKey) {
      try {
        const savedState = localStorage.getItem(storageKey);
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          if (Array.isArray(parsedState) && parsedState.every(item => typeof item === 'string')) {
            const restored = parsedState.filter(
              item => allowedUnitIds.has(item) && selectableUnitIds.has(item)
            );
            if (restored.length !== parsedState.length) {
              localStorage.setItem(storageKey, JSON.stringify(restored));
            }
            return restored;
          }
        }
      } catch (e) {
        console.error('Failed to parse selected units from localStorage', e);
      }
    }

    // Explicit unit selection gate decides first active unit for the session.
    return [];
  }, [allUnits, currentUser, storageKey]);

  const [selectedUnits, setSelectedUnits] = useState<string[]>(getInitialSelection);

  // Re-initialize state if user changes or if allUnits loads after user.
  useEffect(() => {
      if (!currentUser) return;
      setSelectedUnits(prev => {
        const availableUnits = getUserScopedUnits(allUnits, currentUser);
        const allowedUnitIds = getAllowedUnitIds(allUnits, currentUser);
        const selectableUnitIds = new Set(availableUnits.map(unit => unit.id));
        const stillValid = prev.filter(unitId => selectableUnitIds.has(unitId) && allowedUnitIds.has(unitId));
        const fallbackSelection = getFallbackSelectedUnitIds(stillValid, availableUnits);
        if ((stillValid.length !== prev.length || fallbackSelection.join('|') !== prev.join('|')) && storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(fallbackSelection));
          } catch {
            // no-op
          }
        }
        if (fallbackSelection.length > 0) return fallbackSelection;
        return getInitialSelection();
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, allUnits]); // getInitialSelection is memoized and safe here

  // Save changes to localStorage
  useEffect(() => {
    if (storageKey) {
        try {
            localStorage.setItem(storageKey, JSON.stringify(selectedUnits));
        } catch (e) {
            console.error("Failed to save selected units to localStorage", e);
        }
    }
  }, [selectedUnits, storageKey]);


  // Canonical selected-unit objects keep location/timezone consumers on one shared source.
  const selectedUnitObjects = useMemo(() => {
    const byId = new Map(allUnits.map(unit => [unit.id, unit]));
    return selectedUnits.map(unitId => byId.get(unitId)).filter((unit): unit is Unit => Boolean(unit));
  }, [allUnits, selectedUnits]);
  const primaryUnit = selectedUnitObjects[0] ?? null;

  // Memoize the context value to prevent unnecessary re-renders of consumers.
  const value = useMemo(() => ({
    allUnits,
    selectedUnits,
    selectedUnitObjects,
    primaryUnit,
    primaryUnitTimezone: resolveUnitTimezone(primaryUnit),
    setSelectedUnits,
  }), [allUnits, primaryUnit, selectedUnitObjects, selectedUnits]);

  return (
    <UnitContext.Provider value={value}>
      {children}
    </UnitContext.Provider>
  );
};

// Custom hook for easier consumption of the context
export const useUnitContext = () => {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error('useUnitContext must be used within a UnitProvider');
  }
  return context;
};
